import type { PublishingFilters } from './publishing-filters.js';
export const PUBLISHING_LIMITS = {
  dailyMin: 1,
  dailyMax: 144,
  repeatMin: 1,
  repeatMax: 365,
} as const;
export const SELECTION_ORDERS = {
  sequential: 'Сначала неопубликованные, затем давние',
  random: 'Случайная анкета из доступных',
} as const;
export type SelectionOrder = keyof typeof SELECTION_ORDERS;
export const PUBLISHING_PAGE_SIZES = [100, 200, 500] as const;
export type PublishingPageSize = (typeof PUBLISHING_PAGE_SIZES)[number];
export const DEFAULT_MESSAGE_FOOTER = '❤️‍🔥 @ashoo';
export const MAX_MESSAGE_FOOTER = 700;
export interface PublishingMessageSettings {
  footer: string;
  revision: number;
}
export interface PublishingBot {
  id: string;
  name: string;
  username: string;
  telegramId: number;
  revision: number;
}
export interface PublishingBotPermissions {
  botId: string;
  botRevision: number;
  chatId: string;
  checkedAt: number;
}
export interface PublishingSettings {
  title: string;
  botId: string;
  chatId: string;
  cityId: number;
  sourceRevision: number;
  enabled: boolean;
  dailyTarget: number;
  repeatDays: number;
  startTime: string;
  endTime: string;
  filters?: PublishingFilters;
  selectionOrder?: SelectionOrder;
}
export interface PublishingSource {
  id: string;
  workspaceId: string;
  sourceUrl: string;
  citySlug: string;
  cityId: number | null;
  cityError: string | null;
  cityName: string;
  timeZone: string;
  generation: string | null;
  refreshedAt: number | null;
  error: string | null;
  retryAt: number;
  leaseUntil: number;
  leaseToken: string | null;
  filtersJson: string;
}
export interface Profile {
  id: number;
  name: string;
  age: number;
  city: string;
  url: string;
  mediaIds: number[];
}
export interface PublishingChannel extends Omit<PublishingSettings, 'cityId'> {
  messageFooter?: string;
  cityId: number | null;
  sourceUrl: string;
  citySlug: string;
  id: string;
  workspaceId: string;
  sourceId: string;
  cityName: string;
  timeZone: string;
  revision: number;
  nextAt: number;
  error: string | null;
  verifiedAt: number;
}
export interface PublishingChannelView extends Omit<PublishingChannel, 'enabled'> {
  enabled: boolean;
  today: string;
  sentToday: number;
  remaining: number;
  eligible: number;
  catalogueCount: number;
  skippedCount: number;
  refreshedAt: number | null;
  sourceError: string | null;
  blocked: boolean;
  nextPublicationAt: number | null;
}
export type DeliveryStatus =
  'preparing' | 'sending' | 'sent' | 'failed' | 'uncertain' | 'cancelled';
export interface PublishingDelivery {
  id: string;
  channelId: string;
  sourceId: string;
  personId: number;
  day: string;
  revision: number;
  status: DeliveryStatus;
  profileJson: string;
  caption: string;
  createdAt: number;
  finishedAt: number | null;
  messageIdsJson: string;
  error: string | null;
  chatId: string;
  botRevision: number;
}
export interface DeliveryView extends Omit<PublishingDelivery, 'profileJson' | 'messageIdsJson'> {
  profile: Profile;
  messageIds: number[];
  postUrl: string | null;
}
export interface PublishingOverview {
  page: number;
  pageSize: PublishingPageSize;
  total: number;
  messageSettings: PublishingMessageSettings;
  integration: PublishingIntegrationView;
  bots: PublishingBot[];
  channels: PublishingChannelView[];
}
export interface PublishingCity {
  id: number;
  name: string;
  slug: string;
  timeZone: string | null;
}
export interface PublishingIntegrationView {
  sourceUrl: string | null;
  revision: number;
  checkedAt: number | null;
  attemptedUrl: string | null;
  error: string | null;
  candidateUrl: string | null;
  citiesVersion: string | null;
}
export interface PublishingCities {
  sourceUrl: string;
  revision: number;
  version: string;
  items: PublishingCity[];
}
export interface PublishingHistory {
  items: DeliveryView[];
  total: number;
  page: number;
  days: Array<{ day: string; sent: number }>;
}
export interface PublishingPreview {
  profile: Profile | null;
  caption: string | null;
  photos: string[];
  reason: string | null;
}
